import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { debounceTime, map, catchError, switchMap, tap } from 'rxjs/operators';
import { MAuditService } from '../../../services/audit-service/m-audit.service';
import * as AuditActions from './audit.actions';
import { AuditDto, AuditStatus } from '../../../api';
import { NotificationService } from '../../../services/notification-service/notification.service';
import { Router } from '@angular/router';
import { AuditRealtimeService } from '../../../services/audit-realtime-service/audit-realtime.service';

/**
 * Audit Effects
 * 
 * Effects handle side-effects (API calls, routing, notifications).
 * They listen to actions, perform async operations, and dispatch new actions.
 * 
 * Architecture:
 * - Effects use MAuditService (API wrapper with better method names)
 * - MAuditService encapsulates the auto-generated AuditService
 * - Type safety and consistent API across the application
 * 
 * This solves:
 * ✅ Race conditions - switchMap cancels previous requests
 * ✅ Memory leaks - effects are managed by NgRx lifecycle
 * ✅ Testability - effects can be tested in isolation
 * ✅ API encapsulation - endpoint names stay internal
 */
@Injectable()
export class AuditEffects {
  constructor(
    private actions$: Actions,
    private auditService: MAuditService,
    private notificationService: NotificationService,
    private router: Router,
    private auditRealtime: AuditRealtimeService
  ) {}

  /**
   * Load Audits Effect
   * 
   * switchMap = Cancel previous request if new one comes in
   * This FIXES the race condition problem!
   */
  loadAudits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuditActions.loadAudits, AuditActions.refreshAudits),
      switchMap(() =>
        this.auditService.getUserAudits().pipe(
          map((audits) => AuditActions.loadAuditsSuccess({ audits })),
          catchError((error) =>
            of(
              AuditActions.loadAuditsFailure({
                error: error.message || 'Failed to load audits',
              })
            )
          )
        )
      )
    )
  );

  /**
   * Realtime Audit List Refresh Effect
   *
   * Listens to the SignalR hub signal and re-triggers loadAudits.
   * debounceTime prevents a burst of signals (e.g. batch operations)
   * from causing multiple back-to-back API calls.
   */
  realtimeAuditChanged$ = createEffect(() =>
    this.auditRealtime.onAuditChanged().pipe(
      debounceTime(500),
      map(() => AuditActions.refreshAudits())
    )
  );

  /**
   * Load Single Audit Effect
   */
  loadAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuditActions.loadAudit),
      switchMap(({ auditId }) =>
        this.auditService.getAuditById(auditId).pipe(
          map((audit) => AuditActions.loadAuditSuccess({ audit })),
          catchError((error) =>
            of(
              AuditActions.loadAuditFailure({
                error: error.message || 'Failed to load audit',
              })
            )
          )
        )
      )
    )
  );

  /**
   * Create Audit Effect
   */
  createAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuditActions.createAudit),
      switchMap(({ audit }) =>
        this.auditService.createAudit(audit).pipe(
          map((createdAudit) =>
            AuditActions.createAuditSuccess({ audit: createdAudit })
          ),
          catchError((error) => {
            console.error('Create audit failed:', error);
            return of(
              AuditActions.createAuditFailure({
                error: error.message || 'Failed to create audit',
              })
            );
          })
        )
      )
    )
  );

  /**
   * Create Audit Success Effect - Navigate & Notify
   */
  createAuditSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuditActions.createAuditSuccess),
        tap(({ audit }) => {
          console.log('Audit created successfully:', audit.id);
          this.notificationService.showSuccess('AUDIT.SUCCESS_CREATED');
          this.router.navigate(['/audit', audit.id]);
        })
      ),
    { dispatch: false }
  );

  /**
   * Delete Audit Effect
   * 
   * WICHTIG: Die API returned 200 OK mit leerem Body, aber der generierte
   * Client erwartet ein AuditDto. Das führt zu einem Parsing Error (404).
   * 
   * LÖSUNG: Wir catchen ALLE Errors und prüfen den HTTP Status.
   * - Status 200/204/404: Delete war erfolgreich (404 = bereits gelöscht)
   * - Andere Status: Echter Fehler
   */
  deleteAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuditActions.deleteAudit),
      switchMap(({ auditId }) =>
        this.auditService.deleteAudit(auditId).pipe(
          map(() => AuditActions.deleteAuditSuccess({ auditId })),
          catchError((error) => {
            // Parsing Error bei 200 OK = Success!
            if (error.status === 200 || error.status === 204) {
              return of(AuditActions.deleteAuditSuccess({ auditId }));
            }
            
            // Echter Fehler (404, 401, 403, 500, etc.)
            return of(
              AuditActions.deleteAuditFailure({
                error: error.error?.message || error.message || 'Failed to delete audit',
              })
            );
          })
        )
      )
    )
  );

  /**
   * Update Audit Status Effect
   */
  updateAuditStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuditActions.updateAuditStatus),
      switchMap(({ auditId, status }) =>
        this.auditService.updateAuditStatus(auditId, status).pipe(
          map(() => AuditActions.updateAuditStatusSuccess({ auditId, status })),
          catchError(error => {
            console.error('Update audit status failed:', error);
            return of(AuditActions.updateAuditStatusFailure({ 
              error: error.message || 'Failed to update audit status' 
            }));
          })
        )
      )
    )
  );

  /**
   * Update Audit Status Success Effect - Notify
   */
  updateAuditStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuditActions.updateAuditStatusSuccess),
        tap(({ status }) => {
          const messageKey = status === AuditStatus.Archived 
            ? 'AUDIT.STATUS_ARCHIVED' 
            : 'AUDIT.STATUS_UPDATED';
          this.notificationService.showSuccess(messageKey);
        })
      ),
    { dispatch: false }
  );

  /**
   * Bulk Update Audit Status Effect
   */
  bulkUpdateAuditStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuditActions.bulkUpdateAuditStatus),
      switchMap(({ auditIds, status }) =>
        forkJoin(auditIds.map(auditId =>
          this.auditService.updateAuditStatus(auditId, status)
        )).pipe(
          map(() => AuditActions.bulkUpdateAuditStatusSuccess({ auditIds, status })),
          catchError(error =>
            of(AuditActions.bulkUpdateAuditStatusFailure({
              error: error.message || 'Failed to update audit statuses'
            }))
          )
        )
      )
    )
  );

  bulkDeleteAudits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuditActions.bulkDeleteAudits),
      switchMap(({ auditIds }) =>
        forkJoin(auditIds.map(auditId =>
          this.auditService.deleteAudit(auditId).pipe(
            catchError(error => {
              // Same parsing-error workaround as single delete: API returns 200 OK with empty body
              if (error.status === 200 || error.status === 204) {
                return of(null);
              }
              throw error;
            })
          )
        )).pipe(
          map(() => AuditActions.bulkDeleteAuditsSuccess({ auditIds })),
          catchError(error =>
            of(AuditActions.bulkDeleteAuditsFailure({
              error: error.error?.message || error.message || 'Failed to delete audits'
            }))
          )
        )
      )
    )
  );

  bulkDeleteAuditsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuditActions.bulkDeleteAuditsSuccess),
        tap(() => {
          this.notificationService.showSuccess('AUDIT_SELECTION.BULK_DELETE_SUCCESS');
        })
      ),
    { dispatch: false }
  );

  /**
   * Error Handling Effect - Show Notifications
   */
  handleErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AuditActions.loadAuditsFailure,
          AuditActions.loadAuditFailure,
          AuditActions.createAuditFailure,
          AuditActions.deleteAuditFailure,
          AuditActions.updateAuditStatusFailure,
          AuditActions.bulkUpdateAuditStatusFailure,
          AuditActions.bulkDeleteAuditsFailure
        ),
        tap(({ error }) => {
          this.notificationService.showError(undefined, error);
        })
      ),
    { dispatch: false }
  );
}
